import React, { useState, useEffect } from 'react';
import {
    Plus, FileText, ChevronRight, Trash2, Calendar,
    Wrench, DollarSign
} from 'lucide-react';
import { API_BASE_URL } from '../../api/client';
import ManualInvoiceForm from './ManualInvoiceForm';
import ManualInvoiceDetails from './ManualInvoiceDetails';

function GlobalTotals({ invoices }) {
    const totals = invoices.reduce((acc, invoice) => {
        const items = invoice.items || [];
        items.forEach(item => {
            const ht = item.montant_ht || 0;
            const tva = ht * (item.taux_tva || 0) / 100;
            acc.totalFactureHT += ht;
            acc.totalFactureTVA += tva;
            if (item.montant_remboursable) {
                acc.totalRemboursableHT += ht;
                acc.totalRemboursableTVA += tva;
            } else {
                acc.totalNonRemboursableHT += ht;
                acc.totalNonRemboursableTVA += tva;
            }
        });
        return acc;
    }, {
        totalFactureHT: 0,
        totalFactureTVA: 0,
        totalRemboursableHT: 0,
        totalRemboursableTVA: 0,
        totalNonRemboursableHT: 0,
        totalNonRemboursableTVA: 0,
    });

    const totalFactureTTC = totals.totalFactureHT + totals.totalFactureTVA;
    const totalRemboursableTTC = totals.totalRemboursableHT + totals.totalRemboursableTVA;
    const totalNonRemboursableTTC = totals.totalNonRemboursableHT + totals.totalNonRemboursableTVA;

    const fmt = (n) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DT';

    return (
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="font-bold text-gray-800 text-sm">Totaux Généraux du Sinistre</h3>
            </div>

            <div className="divide-y divide-gray-100">
                {/* Total facturé HT */}
                <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-gray-600">Total facturé HT</span>
                    <span className="text-sm text-gray-800">{fmt(totals.totalFactureHT)}</span>
                </div>

                {/* Total facturé TTC */}
                <div className="flex justify-between items-center px-4 py-3 bg-gray-50">
                    <span className="text-sm font-bold text-gray-800">Total facturé TTC</span>
                    <span className="text-sm font-bold text-gray-900">{fmt(totalFactureTTC)}</span>
                </div>

                {/* Total remboursable HT */}
                <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-green-700">Total remboursable HT</span>
                    <span className="text-sm text-green-700">{fmt(totals.totalRemboursableHT)}</span>
                </div>

                {/* Total remboursable TTC */}
                <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm font-bold text-green-700">Total remboursable TTC</span>
                    <span className="text-sm font-bold text-green-700">{fmt(totalRemboursableTTC)}</span>
                </div>

                {/* Total non remboursable HT */}
                <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-red-600">Total non remboursable HT</span>
                    <span className="text-sm text-red-600">{fmt(totals.totalNonRemboursableHT)}</span>
                </div>

                {/* Total non remboursable TTC */}
                <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm font-bold text-red-600">Total non remboursable TTC</span>
                    <span className="text-sm font-bold text-red-600">{fmt(totalNonRemboursableTTC)}</span>
                </div>
            </div>

            {/* NET À REMBOURSER */}
            <div className="flex justify-between items-center px-4 py-4 bg-blue-600">
                <span className="text-sm font-bold text-white uppercase tracking-wide">Net à rembourser</span>
                <span className="text-base font-bold text-white">{fmt(totalRemboursableTTC)}</span>
            </div>
        </div>
    );
}

export default function ManualInvoiceList({ recordId }) {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    useEffect(() => {
        fetchInvoices();
    }, [recordId]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/dossier/${recordId}/manual-invoices`);
            if (response.ok) {
                const data = await response.json();
                setInvoices(data);
            }
        } catch (error) {
            console.error("Error fetching manual invoices:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, invoiceId) => {
        e.stopPropagation();
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette facture et toutes ses lignes ?")) return;

        try {
            const response = await fetch(`${API_BASE_URL}/manual-invoices/${invoiceId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setInvoices(invoices.filter(inv => inv.id !== invoiceId));
                if (selectedInvoice?.id === invoiceId) {
                    setSelectedInvoice(null);
                }
            }
        } catch (error) {
            console.error("Error deleting invoice:", error);
        }
    };

    const handleInvoiceCreated = (newInvoice) => {
        setInvoices([newInvoice, ...invoices]);
        setShowAddForm(false);
    };

    if (selectedInvoice) {
        return (
            <ManualInvoiceDetails
                invoice={selectedInvoice}
                onBack={() => {
                    setSelectedInvoice(null);
                    fetchInvoices(); // Refresh to update totals if needed
                }}
            />
        );
    }

    return (
        <>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Factures Manuelles
                </h3>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                    <Plus className="w-4 h-4" />
                    Nouvelle Facture
                </button>
            </div>

            {showAddForm && (
                <div className="p-4 border-b border-gray-100 bg-blue-50/30">
                    <ManualInvoiceForm
                        recordId={recordId}
                        onSuccess={handleInvoiceCreated}
                        onCancel={() => setShowAddForm(false)}
                    />
                </div>
            )}

            <div className="divide-y divide-gray-100">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Chargement...</div>
                ) : invoices.length === 0 ? (
                    <div className="p-8 text-center flex flex-col items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                            <FileText className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium">Aucune facture manuelle</p>
                        <p className="text-gray-400 text-xs mt-1">Créez une facture pour ajouter des lignes</p>
                    </div>
                ) : (
                    invoices.map((invoice) => {
                        const items = invoice.items || [];
                        const fmtDT = (n) => n.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' DT';

                        const totalHT = items.reduce((sum, item) => sum + (item.montant_ht || 0), 0);
                        const totalTVA = items.reduce((sum, item) => sum + ((item.montant_ht || 0) * (item.taux_tva || 0) / 100), 0);
                        const totalTTC = totalHT + totalTVA;

                        const remboursableHT = items.filter(i => i.montant_remboursable).reduce((sum, item) => sum + (item.montant_ht || 0), 0);
                        const remboursableTVA = items.filter(i => i.montant_remboursable).reduce((sum, item) => sum + ((item.montant_ht || 0) * (item.taux_tva || 0) / 100), 0);
                        const remboursableTTC = remboursableHT + remboursableTVA;

                        const nonRemboursableHT = items.filter(i => !i.montant_remboursable).reduce((sum, item) => sum + (item.montant_ht || 0), 0);
                        const nonRemboursableTVA = items.filter(i => !i.montant_remboursable).reduce((sum, item) => sum + ((item.montant_ht || 0) * (item.taux_tva || 0) / 100), 0);
                        const nonRemboursableTTC = nonRemboursableHT + nonRemboursableTVA;

                        return (
                            <div
                                key={invoice.id}
                                onClick={() => setSelectedInvoice(invoice)}
                                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer group"
                            >
                                <div className="flex justify-between items-start gap-4">
                                    {/* Left: invoice info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-bold text-gray-900 text-lg">
                                                {invoice.numero_facture}
                                            </span>
                                            {invoice.date_facture && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                                    <Calendar className="w-3 h-3" />
                                                    {invoice.date_facture}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                                            <span className="flex items-center gap-1.5">
                                                <Wrench className="w-3.5 h-3.5 text-gray-400" />
                                                {invoice.reparateur_nom}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-100 font-medium">
                                                {items.length} lignes
                                            </span>
                                            {invoice.matricule_fiscale && (
                                                <span className="text-gray-500">
                                                    MF: {invoice.matricule_fiscale}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right: totals */}
                                    <div className="text-right shrink-0">
                                        {/* Total TTC */}
                                        <div className="text-base font-bold text-gray-900 mb-1">
                                            {fmtDT(totalTTC)}
                                        </div>
                                        <div className="text-xs text-gray-400 mb-2">
                                            HT: {fmtDT(totalHT)}
                                        </div>

                                        {/* Remboursable */}
                                        {remboursableTTC > 0 && (
                                            <div className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-100 mb-1">
                                                ✓ Remb. TTC: {fmtDT(remboursableTTC)}
                                            </div>
                                        )}

                                        {/* Non remboursable */}
                                        {nonRemboursableTTC > 0 && (
                                            <div className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100 mb-1">
                                                ✗ Non remb. TTC: {fmtDT(nonRemboursableTTC)}
                                            </div>
                                        )}

                                        <button
                                            onClick={(e) => handleDelete(e, invoice.id)}
                                            className="mt-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                            title="Supprimer la facture"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="self-center text-gray-300">
                                        <ChevronRight className="w-5 h-5" />
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
        {!loading && invoices.length > 0 && <GlobalTotals invoices={invoices} />}
        </>
    );
}